import { ComponentTypes } from 'opensolar-common'
import { AddIcon, Button, CloseIcon, Dialog, DialogTitle, IconButton, styled, Typography, useTheme } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React from 'react'
import { StyledDialogTitle } from './components'

// Bom Selector allows to select "mounting" specific component
export type BomComponentSelectorType = ComponentTypes | 'mounting'

interface AddItemDialogProps {
  handleClose: () => void
  handleSelectCategory: (category: BomComponentSelectorType) => void
  title: string
  content: string
  contentSubtext?: string
}

type ComponentTypeOption = {
  label: string
  value: BomComponentSelectorType
}

const StyledDialogContent = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: 24,
  padding: 24,
  [theme.breakpoints.down('sm')]: {
    padding: 16,
    gap: 16,
  },
}))

const CategorySelectionContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
    gap: 12,
    '& > *': {
      width: '100%',
    },
  },
}))

const CategoryButton = styled(Button)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#FFFFFF',
  color: '#113B98',
  border: '1px solid #E6E6E6',
  gap: 8,
  padding: '8px 13px',
})

const COMPONENT_TYPE_OPTIONS: ComponentTypeOption[] = [
  {
    label: 'Panels',
    value: 'module',
  },
  {
    label: 'Inverter',
    value: 'inverter',
  },
  {
    label: 'Battery',
    value: 'battery',
  },
  {
    label: 'Mounting',
    value: 'mounting',
  },
  {
    label: 'Others',
    value: 'other',
  },
]

const AddItemDialog: React.FC<AddItemDialogProps> = ({
  handleClose,
  handleSelectCategory,
  title,
  content,
  contentSubtext,
}) => {
  const theme = useTheme()
  const translate = useTranslate()
  return (
    <Dialog open={true} onClose={handleClose}>
      <StyledDialogTitle>
        <Typography textVariant="modalTitle" colorHex={theme.palette.neutralGrey[1300] as `#${string}`}>
          {translate(title ?? 'Add New Component')}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon color={theme.palette.neutralGrey[800]} size={16} />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Typography textVariant="body2" colorHex={theme.palette.neutralGrey[1000] as `#${string}`}>
          {content}
        </Typography>
        {contentSubtext && (
          <Typography textVariant="body2" colorHex={theme.palette.neutralGrey[1000] as `#${string}`}>
            {contentSubtext}
          </Typography>
        )}
        <Typography textVariant="subtitle1Bold" colorHex={theme.palette.neutralGrey[1100] as `#${string}`}>
          {translate('Select Category')}
        </Typography>
        <CategorySelectionContainer>
          {COMPONENT_TYPE_OPTIONS.map((item: ComponentTypeOption) => (
            <CategoryButton variant="outlined" color="default" onClick={() => handleSelectCategory(item.value)}>
              <AddIcon color={theme.palette.neutralGrey[900]} height="20" width="20" />
              {translate(item.label)}
            </CategoryButton>
          ))}
        </CategorySelectionContainer>
      </StyledDialogContent>
    </Dialog>
  )
}

export default AddItemDialog
